import { Point } from "@may-i/mash-renderer/refactor";

const distanceSquared = (p1: Point, p2: Point): number => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return dx * dx + dy * dy;
};

const hungarianAlgorithm = (costMatrix: number[][]): number[] => {
  const n = costMatrix.length;
  const m = costMatrix[0].length;
  const u = Array.from({ length: n + 1 }, () => 0);
  const v = Array.from({ length: m + 1 }, () => 0);
  const p = Array.from({ length: m + 1 }, () => 0);
  const way = Array.from({ length: m + 1 }, () => 0);

  for (let i = 1; i <= n; i++) {
    p[0] = i;
    const minv = Array.from({ length: m + 1 }, () => Infinity);
    const used = Array.from({ length: m + 1 }, () => false);
    let j0 = 0;

    do {
      used[j0] = true;
      const i0 = p[j0];
      let delta = Infinity;
      let j1 = -1;
      for (let j = 1; j <= m; j++) {
        if (!used[j]) {
          const cur = costMatrix[i0 - 1][j - 1] - u[i0] - v[j];
          if (cur < minv[j]) {
            minv[j] = cur;
            way[j] = j0;
          }
          if (minv[j] < delta) {
            delta = minv[j];
            j1 = j;
          }
        }
      }

      for (let j = 0; j <= m; j++) {
        if (used[j]) {
          u[p[j]] += delta;
          v[j] -= delta;
        } else {
          minv[j] -= delta;
        }
      }

      j0 = j1!;
    } while (p[j0] !== 0);

    do {
      const j1 = way[j0];
      p[j0] = p[j1];
      j0 = j1;
    } while (j0 !== 0);
  }

  const result = Array.from({ length: n }, () => -1);
  for (let j = 1; j <= m; j++) {
    if (p[j] > 0 && p[j] <= n) {
      result[p[j] - 1] = j - 1;
    }
  }

  return result;
};

export const assignOptimally = (points: Point[], assignablePositions: Point[]): number[] => {
  if (points.length > assignablePositions.length) {
    throw new Error("Assignable positions must be >= points.");
  }

  const costMatrix = points.map((point) =>
    assignablePositions.map((pos) => distanceSquared(point, pos))
  );

  const size = Math.max(points.length, assignablePositions.length);
  const paddedMatrix = Array.from({ length: size }, (_, i) =>
    Array.from({ length: size }, (_, j) =>
      i < points.length && j < assignablePositions.length ? costMatrix[i][j] : 1e10
    )
  );

  return hungarianAlgorithm(paddedMatrix).slice(0, points.length);
};

export const findMaxCount = (outerSize: number, innerSize: number, minGap: number) => {
  let maxCount = 0;
  let count = 1;

  while (outerSize > innerSize * count + minGap * (count - 1)) {
    maxCount = count;
    count += 1;
  }

  return maxCount;
};
